import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import TilesGrid from '../tiles/TilesGrid';
import {Tile as GridTile, TilesGridConfig} from '../tiles/types';
import {Tile as BackendTile, TileType} from '../../admin/publications/types/Tile';
import {handleError} from '../../common/utils/errorHandling';
import {WideHeader} from '../../layout/wide';
import tilesService from '../../admin/publications/services/tilesService';
import artisticObjectsService from '../../admin/artisticobjects/services/artisticObjectsService';
import placesService from '../../admin/artisticobjects/services/placesService';
import collectionsService from '../../admin/publications/services/collectionsService';
import {getValueWithFallback} from '../../common/types/StringTranslations';

const HomePage: React.FC = () => {
  const {t, i18n} = useTranslation();
  const [gridConfig, setGridConfig] = useState<TilesGridConfig>({tiles: []});

  useEffect(() => {
    fetchTiles();
  }, []);

  const fetchTiles = async (): Promise<void> => {
    await handleError(
      async () => {
        const backendTiles = await tilesService.getAllTiles();
        const gridTiles = await Promise.all(backendTiles.map(tile => convertToGridTile(tile)));
        setGridConfig({tiles: gridTiles});
      },
      t('common.errors.fetchingTiles')
    );
  };

  const convertToGridTile = async (tile: BackendTile): Promise<GridTile> => {
    switch (tile.type) {
      case TileType.ARTISTIC_OBJECT:
        return await createArtisticObjectTile(tile);
      case TileType.PLACE:
        return await createPlaceTile(tile);
      case TileType.COLLECTION:
        return await createCollectionTile(tile);
      case TileType.PATH:
        return createPathTile(tile);
      default:
        return createPathTile(tile);
    }
  };

  const getTileImageData = (tile: BackendTile) => {
    if (tile.image) {
      return {
        imagePath: tile.image.thumbnailPath || tile.image.path,
        focalPoint: tile.image.focalPoint,
        size: {width: tile.image.width, height: tile.image.height}
      };
    }
    return null;
  };

  const createArtisticObjectTile = async (tile: BackendTile): Promise<GridTile> => {
    if (!tile.artisticObjectId) {
      return createFallbackTile(tile);
    }

    const artisticObject = await artisticObjectsService.getArtisticObject(tile.artisticObjectId);
    const tileImageData = getTileImageData(tile);
    const firstImage = tileImageData ? null : artisticObject.images?.[0];
    const imagePath = tileImageData?.imagePath
      || firstImage?.thumbnailPath
      || firstImage?.path
      || 'images/placeholder.jpg';
    const description = artisticObject.sections?.[0]?.content || artisticObject.locationDescription || artisticObject.dimensions || '';
    const tileName = getValueWithFallback(tile.name, i18n.language);
    const tileDescription = getValueWithFallback(tile.description, i18n.language);
    const tileTag = getValueWithFallback(tile.tag, i18n.language);

    return {
      id: artisticObject.id,
      image: imagePath,
      title: tileName || getValueWithFallback(artisticObject.name, i18n.language),
      description: tileDescription || description,
      width: tile.width,
      height: tile.height,
      tag: tileTag || undefined,
      linkPath: `/gallery/artistic-object/${artisticObject.id}`,
      focalPoint: tileImageData ? tileImageData.focalPoint : firstImage?.focalPoint,
      size: tileImageData ? tileImageData.size : (firstImage ? {
        width: firstImage.width,
        height: firstImage.height
      } : undefined)
    };
  };

  const createPlaceTile = async (tile: BackendTile): Promise<GridTile> => {
    if (!tile.placeId) {
      return createFallbackTile(tile);
    }

    const place = await placesService.getPlace(tile.placeId);
    const tileImageData = getTileImageData(tile);
    const firstImage = tileImageData ? null : (place.images?.[0] || place.firstImage);
    const imagePath = tileImageData?.imagePath
      || firstImage?.thumbnailPath
      || firstImage?.path
      || 'images/placeholder.jpg';
    const placeName = getValueWithFallback(place.name, i18n.language);
    const tileName = getValueWithFallback(tile.name, i18n.language);
    const tileDescription = getValueWithFallback(tile.description, i18n.language);
    const tileTag = getValueWithFallback(tile.tag, i18n.language);

    return {
      id: place.id,
      image: imagePath,
      title: tileName || placeName,
      description: tileDescription || '',
      width: tile.width,
      height: tile.height,
      tag: tileTag || undefined,
      linkPath: `/gallery/place/${place.id}`,
      focalPoint: tileImageData ? tileImageData.focalPoint : firstImage?.focalPoint,
      size: tileImageData ? tileImageData.size : (firstImage ? {
        width: firstImage.width,
        height: firstImage.height
      } : undefined)
    };
  };

  const createCollectionTile = async (tile: BackendTile): Promise<GridTile> => {
    if (!tile.collectionId) {
      return createFallbackTile(tile);
    }

    const collection = await collectionsService.getCollection(tile.collectionId);
    const tileImageData = getTileImageData(tile);
    let imagePath = 'images/placeholder.jpg';
    let focalPoint = undefined;
    let size = undefined;

    if (tileImageData) {
      imagePath = tileImageData.imagePath;
      focalPoint = tileImageData.focalPoint;
      size = tileImageData.size;
    } else {
      const items = await collectionsService.getCollectionItems(tile.collectionId);
      const firstImage = items[0]?.artisticObject?.firstImage;
      if (firstImage) {
        imagePath = firstImage.thumbnailPath || firstImage.path || 'images/placeholder.jpg';
        focalPoint = firstImage.focalPoint;
        size = {width: firstImage.width, height: firstImage.height};
      }
    }

    const tileName = getValueWithFallback(tile.name, i18n.language);
    const tileDescription = getValueWithFallback(tile.description, i18n.language);
    const tileTag = getValueWithFallback(tile.tag, i18n.language);
    const collectionTitle = getValueWithFallback(collection.title, i18n.language);
    const collectionSubtitle = getValueWithFallback(collection.subtitle, i18n.language);
    const collectionDescription = getValueWithFallback(collection.description, i18n.language);

    return {
      id: collection.id,
      image: imagePath,
      title: tileName || collectionTitle,
      subtitle: collectionSubtitle,
      description: tileDescription || collectionDescription || '',
      width: tile.width,
      height: tile.height,
      tag: tileTag || undefined,
      linkPath: `/browse-collections/${collection.id}`,
      focalPoint,
      size
    };
  };

  const createPathTile = (tile: BackendTile): GridTile => {
    const tileImageData = getTileImageData(tile);
    const tileName = getValueWithFallback(tile.name, i18n.language);
    const tileDescription = getValueWithFallback(tile.description, i18n.language);
    const tileTag = getValueWithFallback(tile.tag, i18n.language);

    return {
      id: tile.id,
      image: tileImageData?.imagePath || 'images/placeholder.jpg',
      title: tileName,
      description: tileDescription || '',
      width: tile.width,
      height: tile.height,
      tag: tileTag || undefined,
      linkPath: tile.path || '/',
      focalPoint: tileImageData?.focalPoint,
      size: tileImageData?.size
    };
  };

  const createFallbackTile = (tile: BackendTile): GridTile => {
    const tileImageData = getTileImageData(tile);
    const tileName = getValueWithFallback(tile.name, i18n.language);
    const tileDescription = getValueWithFallback(tile.description, i18n.language);
    const tileTag = getValueWithFallback(tile.tag, i18n.language);

    return {
      id: tile.id,
      image: tileImageData?.imagePath || 'images/placeholder.jpg',
      title: tileName,
      description: tileDescription || '',
      width: tile.width,
      height: tile.height,
      tag: tileTag || undefined,
      focalPoint: tileImageData?.focalPoint,
      size: tileImageData?.size
    };
  };

  return (
    <>
      <WideHeader
        title={t('home.latestDiscoveries.title')}
        introduction={t('home.latestDiscoveries.introduction')}
      />
      <TilesGrid config={gridConfig}/>
    </>
  );
};

export default HomePage;

